import React, { useEffect, useState, useRef } from 'react';
import { Users, CalendarDays, Loader2, Download, ClipboardList, BookOpen, Wifi, Film, Trophy, Target, CheckCircle2, Circle, Camera, Activity, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { DailyQuote } from './DailyQuote';
import api from '../services/api';
import TeacherVault from './TeacherVault';
import LiveClassCard from './LiveClassCard';
import AssignCombine from './AssignCombine';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const token = () => localStorage.getItem('academy_token') || '';

const resizeToDataUrl = (file, size = 256) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      const m = Math.min(img.width, img.height);
      ctx.drawImage(img, (img.width - m) / 2, (img.height - m) / 2, m, m, 0, 0, size, size);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = reject; img.src = e.target.result;
  };
  reader.onerror = reject; reader.readAsDataURL(file);
});

const SUBJECT_COLORS = {
  Mathematics: 'text-sky-400 border-sky-500/40',
  'English Language Arts': 'text-amber-400 border-amber-500/40',
  Science: 'text-emerald-400 border-emerald-500/40',
  'Social Studies': 'text-orange-400 border-orange-500/40',
  'Cross-Disciplinary Application': 'text-fuchsia-400 border-fuchsia-500/40',
};

const fmtDT = (iso) => {
  if (!iso) return '—';
  try { const d = new Date(iso); return isNaN(d) ? '—' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return '—'; }
};

const RecentActivityCell = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const load = () => {
    setBusy(true);
    api.get('/academy/teacher/activity', { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load activity'))
      .finally(() => setBusy(false));
  };
  const toggle = () => { const n = !open; setOpen(n); if (n && !data) load(); };

  const s = data?.summary;
  const accounts = data?.accounts || [];

  return (
    <div className="border-2 border-teal-500/40 bg-gradient-to-br from-teal-500/[0.06] to-transparent" data-testid="teacher-activity-cell">
      <button onClick={toggle} className="w-full flex items-center justify-between gap-4 p-4 text-left" data-testid="teacher-activity-toggle">
        <div className="flex items-center gap-3 min-w-0">
          <Activity className="w-6 h-6 text-teal-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-[0.3em] text-teal-400 font-black">Recent Activity</div>
            <div className="text-sm font-black text-white mt-0.5 truncate">Sign-ups &amp; last-active — tap to {open ? 'collapse' : 'expand'}</div>
          </div>
        </div>
        {open ? <ChevronDown className="w-5 h-5 text-teal-400 flex-shrink-0" /> : <ChevronRight className="w-5 h-5 text-teal-400 flex-shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-4" data-testid="teacher-activity-body">
          {busy && !data ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-teal-400" /></div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 flex-1">
                  {[['Total', s?.total, '#2DD4BF'], ['Students', s?.students, '#38BDF8'], ['Active 24h', s?.active_24h, '#34D399'], ['Active 7d', s?.active_7d, '#A3E635'], ['New 7d', s?.new_7d, '#F59E0B']].map(([label, val, color]) => (
                    <div key={label} className="bg-black/40 border border-zinc-800 px-2 py-2 rounded text-center" data-testid={`teacher-activity-stat-${label}`}>
                      <div className="text-lg font-black" style={{ color }}>{val ?? '—'}</div>
                      <div className="text-[8px] uppercase tracking-wider text-zinc-500">{label}</div>
                    </div>
                  ))}
                </div>
                <button onClick={load} disabled={busy} className="ml-3 text-zinc-500 hover:text-white flex-shrink-0" data-testid="teacher-activity-refresh" title="Refresh">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-zinc-600 mb-2">"Last Active" comes from the student-dashboard presence ping (there is no separate login log yet).</p>
              {accounts.length === 0 ? (
                <div className="text-xs text-zinc-600 py-4 text-center border-2 border-dashed border-zinc-800">No accounts yet.</div>
              ) : (
                <div className="max-h-80 overflow-y-auto border border-zinc-800 rounded">
                  <table className="w-full text-left">
                    <thead className="sticky top-0 bg-[#0d0d10]">
                      <tr className="text-[9px] uppercase tracking-wider text-zinc-500">
                        <th className="px-3 py-2">Account</th><th className="px-3 py-2">Role</th><th className="px-3 py-2">Grade · State</th><th className="px-3 py-2">Joined</th><th className="px-3 py-2">Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.map((u, i) => (
                        <tr key={u.id || i} className="border-t border-zinc-900 text-xs" data-testid={`teacher-activity-row-${i}`}>
                          <td className="px-3 py-2"><div className="text-white font-medium truncate max-w-[180px]">{u.name || '—'}</div><div className="text-zinc-500 font-mono text-[10px] truncate max-w-[180px]">{u.email}</div></td>
                          <td className="px-3 py-2"><span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${u.role === 'teacher' ? 'bg-orange-500/20 text-orange-300' : u.role === 'student' ? 'bg-sky-500/20 text-sky-300' : 'bg-zinc-700/40 text-zinc-400'}`}>{u.role}</span></td>
                          <td className="px-3 py-2 text-zinc-400">{u.grade_level ? `G${u.grade_level}` : '—'}{u.us_state ? ` · ${u.us_state}` : ''}</td>
                          <td className="px-3 py-2 text-zinc-400">{fmtDT(u.created_at)}</td>
                          <td className="px-3 py-2"><span className={u.last_seen ? 'text-emerald-400' : 'text-zinc-600'}>{fmtDT(u.last_seen)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};


const TeacherHub = () => {
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dl, setDl] = useState(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileRef = useRef(null);

  const loadHub = () => api.get('/academy/teacher/hub', { headers: authHeaders() }).then((r) => setHub(r.data)).catch(() => {});

  useEffect(() => {
    loadHub().finally(() => setLoading(false));
  }, []);

  const onAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setAvatarBusy(true);
    try {
      const dataUrl = await resizeToDataUrl(file);
      await api.post('/school/avatar', { image_data: dataUrl }, { headers: authHeaders() });
      toast.success('Coach photo updated');
      await loadHub();
    } catch { toast.error('Could not upload photo'); } finally { setAvatarBusy(false); }
  };

  const downloadLessonPdf = async (grade) => {
    setDl(grade);
    try {
      const r = await api.get(`/academy/teacher/lesson-pdf?grade=${grade}`, { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `SAA-Grade${grade}-Lesson.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ } finally { setDl(null); }
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>;
  if (!hub) return null;

  const preSeason = hub.day_number === 0;

  return (
    <div className="space-y-8" data-testid="teacher-hub">
      {/* Daily rotating motivation — coaches, athletes, thought leaders */}
      <DailyQuote audience="teacher" variant="dark" />

      {/* Today's Showcase Lesson — pinned demo (Gravity Spiral, K -> 4th -> 6th) */}
      <a
        href="/lesson-samples/saa-gravity-spiral-demo.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="block border-2 border-fuchsia-500/50 bg-gradient-to-r from-fuchsia-600/20 via-emerald-500/10 to-transparent p-5 hover:border-fuchsia-400 transition-colors group"
        data-testid="showcase-lesson-card"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-12 h-12 flex items-center justify-center bg-fuchsia-500/20 border-2 border-fuchsia-500/50 flex-shrink-0">
              <Film className="w-6 h-6 text-fuchsia-300" />
            </div>
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.3em] text-fuchsia-300 font-black">🎬 Today's Showcase Lesson · Recording</div>
              <div className="text-lg font-black text-white mt-1 leading-tight">Gravity Game: The Pull That Makes Every Athlete Better</div>
              <div className="text-[11px] text-zinc-400 mt-1">Upward spiral · Kindergarten → 4th → 6th · sports-centric + whiteboard · NGSS-aligned</div>
            </div>
          </div>
          <span className="flex-shrink-0 flex items-center gap-2 bg-fuchsia-500 group-hover:bg-fuchsia-400 text-white px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors" data-testid="showcase-view-btn">
            <BookOpen className="w-4 h-4" /> View / Teach Lesson
          </span>
        </div>
      </a>

      {/* Gamified header */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="teacher-hub-header">
        <div className="border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-transparent p-4 flex items-center gap-4">
          <button
            onClick={() => fileRef.current?.click()}
            className="relative group w-16 h-16 rounded-full overflow-hidden border-2 border-emerald-500/60 bg-black/50 flex items-center justify-center flex-shrink-0"
            data-testid="coach-avatar-btn"
            title="Upload coach photo"
          >
            {hub.teacher_avatar
              ? <img src={hub.teacher_avatar} alt="coach" className="w-full h-full object-cover" />
              : <span className="text-xl font-black text-emerald-300">{(hub.teacher_name || 'C').charAt(0).toUpperCase()}</span>}
            <span className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              {avatarBusy ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
            </span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onAvatar} className="hidden" data-testid="coach-avatar-input" />
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-[0.3em] text-emerald-400 font-black">{hub.teacher_title || 'Head Coach'}</div>
            <div className="text-2xl font-black text-white mt-1 truncate" data-testid="teacher-name">{hub.teacher_name}</div>
            <div className="text-[11px] text-zinc-500 mt-1">Scholar Athlete Academy · 2026–27</div>
          </div>
        </div>
        <div className="border-2 border-sky-500/40 bg-gradient-to-br from-sky-500/10 to-transparent p-4 flex items-center gap-4" data-testid="school-day-counter">
          <CalendarDays className="w-8 h-8 text-sky-400" />
          <div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-sky-400 font-black">School Day</div>
            {preSeason
              ? <div className="text-lg font-black text-white mt-1">Summer · starts Sept 8</div>
              : <div className="text-3xl font-black text-white leading-none mt-1">{hub.day_number}<span className="text-lg text-zinc-500"> / {hub.total_days}</span></div>}
          </div>
        </div>
        <div className="border-2 border-orange-500/40 bg-gradient-to-br from-orange-500/10 to-transparent p-4 flex items-center gap-4" data-testid="students-online">
          <div className="relative"><Users className="w-8 h-8 text-orange-400" /><Wifi className="w-3.5 h-3.5 text-emerald-400 absolute -right-1 -top-1" /></div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-orange-400 font-black">Students Online</div>
            <div className="text-3xl font-black text-white leading-none mt-1">{hub.online_count}<span className="text-lg text-zinc-500"> / {hub.enrolled_count}</span></div>
            <div className="text-[10px] text-zinc-600">logged in now / enrolled</div>
          </div>
        </div>
      </div>

      {/* Expandable: recent account activity (sign-ups + last-active) */}
      <RecentActivityCell />

      {/* Always-on Live Class control — Go Live now or schedule a class (Jitsi) */}
      <LiveClassCard isTeacher={true} />

      {/* Assign Combine reps (homework) */}
      <AssignCombine />

      {/* Coach Locker — gamified teacher cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="coach-locker">
        <div className="border-2 border-emerald-500/40 bg-black/40 p-4" data-testid="coach-card">
          <div className="flex items-center gap-2"><Trophy className="w-4 h-4 text-emerald-400" /><span className="text-[9px] uppercase tracking-[0.3em] text-emerald-400 font-black">Coach Card</span></div>
          <div className="mt-2 flex items-end gap-2"><span className="text-3xl font-black text-white leading-none">Lv {hub.coach?.level ?? 1}</span><span className="text-xs text-zinc-500 mb-0.5">{hub.coach?.xp ?? 0} Scholar Dollars</span></div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div><div className="text-lg font-black text-white">{hub.coach?.days_coaching ?? 0}</div><div className="text-[8px] uppercase tracking-wider text-zinc-600">Days</div></div>
            <div><div className="text-lg font-black text-white">{hub.coach?.plans_run ?? 0}</div><div className="text-[8px] uppercase tracking-wider text-zinc-600">Plans</div></div>
            <div><div className="text-lg font-black text-white">{hub.coach?.docs_sent ?? 0}</div><div className="text-[8px] uppercase tracking-wider text-zinc-600">Sent</div></div>
          </div>
        </div>
        <div className="border-2 border-orange-500/40 bg-black/40 p-4" data-testid="team-quests">
          <div className="flex items-center gap-2 mb-2"><Target className="w-4 h-4 text-orange-400" /><span className="text-[9px] uppercase tracking-[0.3em] text-orange-400 font-black">Today's Coach Quests</span></div>
          <div className="space-y-1.5">
            {(hub.team_quests || []).map((q, i) => (
              <div key={i} className="flex items-center gap-2 text-xs" data-testid={`quest-${i}`}>
                {q.done ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : <Circle className="w-4 h-4 text-zinc-600 flex-shrink-0" />}
                <span className={q.done ? 'text-zinc-500 line-through' : 'text-zinc-200'}>{q.label}</span>
                <span className="ml-auto text-[10px] text-orange-400 font-bold">+{q.xp}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border-2 border-sky-500/40 bg-black/40 p-4" data-testid="class-mastery">
          <div className="flex items-center gap-2 mb-3"><Target className="w-4 h-4 text-sky-400" /><span className="text-[9px] uppercase tracking-[0.3em] text-sky-400 font-black">Class Mastery</span></div>
          <div className="space-y-2">
            {(hub.class_mastery || []).map((m) => (
              <div key={m.group} data-testid={`mastery-${m.group}`}>
                <div className="flex justify-between text-[10px] mb-0.5"><span className="text-zinc-400 font-bold">{m.label}</span><span className="text-white font-black">{m.score}%</span></div>
                <div className="h-2 bg-zinc-800"><div className="h-2 bg-gradient-to-r from-sky-500 to-emerald-400" style={{ width: `${m.score}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Week at a glance */}
      <div data-testid="week-at-a-glance">
        <div className="flex items-center gap-2 mb-3"><CalendarDays className="w-4 h-4 text-emerald-400" /><h3 className="text-sm font-black uppercase tracking-[0.2em]">Week at a Glance</h3></div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {hub.week.map((d) => (
            <div key={d.date} className={`border-2 p-3 ${d.is_today ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-800 bg-black/30'} ${!d.is_school_day ? 'opacity-40' : ''}`} data-testid={`week-day-${d.short}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-black uppercase text-white">{d.short}</span>
                <span className="text-[10px] text-zinc-500">{d.label}</span>
              </div>
              <div className={`mt-1 text-[10px] font-bold uppercase tracking-wide ${(SUBJECT_COLORS[d.subject] || 'text-zinc-400').split(' ')[0]}`}>{d.is_friday ? 'Lab / Enrichment' : d.subject}</div>
              <div className="mt-2 border-t border-zinc-800 pt-2">
                <div className="text-[9px] uppercase tracking-widest text-zinc-600 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Supplies</div>
                <ul className="mt-1 space-y-0.5">
                  {(d.supplies || []).slice(0, 3).map((s, i) => <li key={i} className="text-[10px] text-zinc-400 leading-tight">• {s}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Today's lessons by grade */}
      <div data-testid="todays-lessons">
        <div className="flex items-center gap-2 mb-1"><BookOpen className="w-4 h-4 text-emerald-400" /><h3 className="text-sm font-black uppercase tracking-[0.2em]">Today's Lessons <span className="text-emerald-400">· {hub.subject_today}</span></h3></div>
        <p className="text-xs text-zinc-500 mb-4">{hub.daily_theme}</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {hub.lessons.map((l) => (
            <div key={l.group} className="border-2 border-zinc-800 bg-black/40 p-4 flex flex-col" data-testid={`lesson-${l.group}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-black uppercase tracking-widest text-white">{l.grade_label}</span>
                <span className="text-[10px] font-mono text-emerald-400">{l.standard_code}</span>
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">{l.standard_text}</div>
              <div className="mt-3 border-l-2 border-emerald-500/50 pl-3 text-xs text-zinc-300 leading-relaxed">{l.mini_lesson}</div>
              <div className="mt-3">
                <div className="text-[9px] uppercase tracking-widest text-zinc-600 mb-1">Check for Understanding</div>
                <ol className="space-y-1 list-decimal list-inside">
                  {(l.prompts || []).map((p, i) => <li key={i} className="text-[11px] text-zinc-400 leading-tight">{p}</li>)}
                </ol>
              </div>
              <button onClick={() => downloadLessonPdf(l.grade_label.replace(/\D/g, '') || '5')} disabled={dl !== null} className="mt-4 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:bg-zinc-700 text-black py-2.5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors" data-testid={`download-lesson-${l.group}`}>
                {dl === (l.grade_label.replace(/\D/g, '') || '5') ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download Lesson Plan
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Document Vault */}
      <TeacherVault />
    </div>
  );
};

export default TeacherHub;
